<script setup lang="ts">
import { computed, watch } from 'vue'
import { useIdeStore } from '@/stores/ide.store'
import { useJdroidStore } from '@/stores/jdroid.store'
import TreeItem from '@/components/ide/ide/TreeItemComp.vue'
import addFileIcon from '@/assets/images/ide/file-plus.svg'
import addFolderIcon from '@/assets/images/ide/folder-plus.svg'
import minimizeIcon from '@/assets/images/ide/arrows-minimize.svg'
import { isEmpty } from 'lodash-es'
import { useProjectManager } from '@/stores/projectManager.store'

const ideStore = useIdeStore()
const jdroidStore = useJdroidStore()
const projectManagerStore = useProjectManager()

const httpError = computed(() => {
  return ideStore.syncErrorMessages
})
const isAdvanced = computed(() => {
  return ideStore.isAdvanced
})
const isFullScreen = computed(() => {
  return ideStore.isFullScreen
})
const treeData = computed(() => {
  return ideStore.project?.treeData || {}
})
const treeHome = computed(() => {
  return ideStore.project?.home || {}
})
const currentMobileTab = computed(() => {
  return ideStore.currentMobileTab
})
const disableCollabAccess = computed(() => {
  return projectManagerStore.projectPermissionWithId?.readOnly
})
/**
 * get the parent name
 * @param item tree item
 * @returns parent name
 */
const getParentName = (item: any) => {
  let parent = '/'
  if (item.parent && item.parent === '/') {
    parent = item.parent + item.name
  } else if (item.parent) {
    parent = item.parent + '/' + item.name
  }
  return parent
}
/**
 * forcus the input
 * @param count count
 */
const postGetForcusInput = async (count: number = 0) => {
  const input = document.getElementById('editInput')
  if (!input) {
    if (count < 20) {
      await new Promise((resolve) => setTimeout(resolve, 100))
      postGetForcusInput(count + 1)
    }
  } else {
    input.focus()
  }
}
/**
 * forcus the input
 */
const forcusInput = async () => {
  await postGetForcusInput()
}
/**
 * add a new file
 */
const addFile = () => {
  ideStore.setEditingInProgress(true)
  const item = treeData.value || {}
  let parentName = getParentName(item)
  item?.children.push({
    name: '',
    editMode: true,
    parent: parentName,
    markedForDeletion: false
  })
  forcusInput()
}
/**
 * add a new folder
 */
const addFolder = () => {
  ideStore.setEditingInProgress(true)
  const item = treeData.value || {}
  let parentName = getParentName(item)
  item?.children.push({
    name: '',
    editMode: true,
    parent: parentName,
    markedForDeletion: false,
    children: []
  })
  forcusInput()
}
/**
 * @description Collapses the project tree
 */
const collapseProjectTree = async () => {
  ideStore.setProjectTreeCollapsed(true)

  if (!jdroidStore.showJdroid && currentMobileTab.value == null) {
    await new Promise((resolve) => setTimeout(resolve, 200))
  }
}

watch(
  () => treeData.value,
  (val) => {
    if (isEmpty(val)) {
      ideStore.setCleanUniqueActiveItemAdvIde()
    }
  }
)
watch(
  () => isAdvanced.value,
  () => {
    ideStore.setCleanUniqueActiveItemAdvIde()
  }
)
</script>

<template>
  <div
    :class="[
      'stroke-primary-default',
      'border-r',
      'h-full',
      'w-full',
      'relative',
      // 'bg-slate-200',
      'flex flex-col',
      // 'dark:bg-black',

      {
        'lg:h-full': isFullScreen
      },
      {
        'h-full': !isFullScreen
      }
    ]"
  >
    <div
      class="stroke-primary-default flex w-full items-center justify-between border-b px-1.5 py-2.5"
    >
      <div class="flex w-fit items-center gap-3">
        <button :disabled="disableCollabAccess" @click="addFile">
          <div class="h-5 w-5" :class="{ 'cursor-not-allowed': disableCollabAccess }">
            <img :src="addFileIcon" />
          </div>
        </button>

        <button :disabled="disableCollabAccess" @click="addFolder">
          <div class="h-5 w-5" :class="{ 'cursor-not-allowed': disableCollabAccess }">
            <img :src="addFolderIcon" />
          </div>
        </button>
      </div>

      <button @click="collapseProjectTree">
        <div class="h-5 w-5">
          <img :src="minimizeIcon" />
        </div>
      </button>
    </div>
    <div :class="['background-primary h-full', 'w-full', 'flex', 'flex-col', 'mx-0']">
      <p
        :class="['p-xsmall error max-w-xs overflow-auto break-normal py-2', { hidden: !httpError }]"
      >
        {{ httpError }}
      </p>
      <!-- overflow-scroll -->
      <div class="h-full w-full overflow-auto py-2">
        <TreeItem :treeData="treeData" :treeHome="treeHome" />
      </div>
    </div>
  </div>
</template>
